import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Phone, ChevronRight, Plus } from "lucide-react";
import { STAGES, PLAN_META, formatBRL } from "@/data/backofficeData";
import { LeadSlideOver } from "./LeadSlideOver";
import { LeadForm } from "./LeadForm";

export const CrmBoard = ({ leads, setLeads, token }) => {
  const [openLead, setOpenLead] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editingLead, setEditingLead] = useState(null);

  const API = process.env.REACT_APP_BACKEND_URL;
  const auth = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };
  const patchLead = (id, body) =>
    fetch(`${API}/api/backoffice/leads/${id}`, { method: "PATCH", headers: auth, body: JSON.stringify(body) }).catch(() => {});

  const moveLead = (id, stageId) => {
    setLeads((list) => list.map((l) => (l.id === id ? { ...l, stage: stageId } : l)));
    patchLead(id, { stage: stageId });
  };
  const updateLead = (id, patch) => {
    setLeads((list) => list.map((l) => (l.id === id ? { ...l, ...patch } : l)));
    patchLead(id, patch);
  };
  const addHistory = (id, entry) => {
    setLeads((list) => list.map((l) => (l.id === id ? { ...l, history: [{ id: `h${Date.now()}`, ...entry }, ...l.history] } : l)));
    patchLead(id, { history_entry: entry });
  };

  const removeLead = (id) => {
    setLeads((list) => list.filter((l) => l.id !== id));
    setOpenLead(null);
    fetch(`${API}/api/backoffice/leads/${id}`, { method: "DELETE", headers: auth }).catch(() => {});
    toast.success("Lead removido do funil");
  };

  const saveLead = async (data) => {
    if (editingLead) {
      updateLead(editingLead.id, data);
      toast.success("Lead atualizado", { description: data.name });
    } else {
      try {
        const r = await fetch(`${API}/api/backoffice/leads`, { method: "POST", headers: auth, body: JSON.stringify(data) });
        const d = await r.json();
        if (!r.ok) throw new Error();
        setLeads((list) => [d.lead, ...list]);
        toast.success("Lead adicionado ao funil", { description: data.name });
      } catch {
        toast.error("Falha ao salvar o lead.");
      }
    }
    setEditingLead(null);
  };

  const openNew = () => { setEditingLead(null); setFormOpen(true); };
  const openEdit = (lead) => { setEditingLead(lead); setOpenLead(null); setFormOpen(true); };

  const current = leads.find((l) => l.id === openLead);

  return (
    <div data-testid="crm-board">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <span className="font-semibold text-slate-700">Funil de vendas</span>
          <span>· clique no lead para abrir · use a seta para avançar estágio</span>
        </div>
        <button
          data-testid="crm-new-lead-btn"
          onClick={openNew}
          className="flex items-center gap-1.5 rounded-xl bg-slate-900 px-3.5 py-2.5 text-sm font-bold text-white shadow-sm transition-colors hover:bg-slate-800"
        >
          <Plus className="h-4 w-4" /> Novo Lead
        </button>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5">
        {STAGES.map((stage) => {
          const items = leads.filter((l) => l.stage === stage.id);
          const total = items.reduce((s, l) => s + l.value, 0);
          return (
            <div key={stage.id} data-testid={`crm-col-${stage.id}`} className={`rounded-2xl border border-slate-200 border-t-4 bg-slate-50/60 ${stage.accent}`}>
              <div className="flex items-center justify-between px-4 py-3">
                <div>
                  <p className="text-sm font-bold text-slate-800">{stage.label}</p>
                  <p className="text-[11px] text-slate-400">{items.length} lead(s) · {formatBRL(total)}</p>
                </div>
              </div>
              <div className="space-y-2.5 px-3 pb-3">
                {items.length === 0 && (
                  <p className="rounded-xl border border-dashed border-slate-200 py-6 text-center text-xs text-slate-300">Vazio</p>
                )}
                {items.map((l) => {
                  const idx = STAGES.findIndex((s) => s.id === l.stage);
                  const next = STAGES[idx + 1];
                  return (
                    <motion.div key={l.id} layout data-testid={`lead-card-${l.id}`} className="group rounded-xl border border-slate-200 bg-white p-3 shadow-sm transition-shadow hover:shadow-md">
                      <button onClick={() => setOpenLead(l.id)} data-testid={`lead-open-${l.id}`} className="block w-full text-left">
                        <div className="flex items-start justify-between gap-2">
                          <p className="text-sm font-semibold text-slate-900">{l.name}</p>
                          <span className={`shrink-0 rounded-full border px-1.5 py-0.5 text-[10px] font-bold ${PLAN_META[l.plan].color}`}>{PLAN_META[l.plan].label}</span>
                        </div>
                        <p className="mt-0.5 text-xs text-slate-500">{l.contact}</p>
                        <p className="mt-1 flex items-center gap-1 text-[11px] text-slate-400"><Phone className="h-3 w-3" />{l.phone}</p>
                        <p className="mt-1.5 font-mono-num text-xs font-bold text-slate-700">{formatBRL(l.value)}/mês</p>
                      </button>
                      {next && (
                        <button
                          data-testid={`lead-advance-${l.id}`}
                          onClick={() => moveLead(l.id, next.id)}
                          className="mt-2 flex w-full items-center justify-center gap-1 rounded-lg bg-slate-900 py-1.5 text-[11px] font-semibold text-white opacity-100 transition-opacity hover:bg-slate-800 lg:opacity-0 lg:group-hover:opacity-100"
                        >
                          Mover p/ {next.label} <ChevronRight className="h-3 w-3" />
                        </button>
                      )}
                    </motion.div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      <LeadSlideOver
        lead={current}
        onClose={() => setOpenLead(null)}
        onMove={(stageId) => moveLead(current.id, stageId)}
        onHistory={(entry) => addHistory(current.id, entry)}
        onEdit={() => openEdit(current)}
        onRemove={() => removeLead(current.id)}
      />

      <LeadForm open={formOpen} onOpenChange={setFormOpen} editing={editingLead} onSave={saveLead} />
    </div>
  );
};
